import { serverNow } from '../hooks/useServerNow';

// Lesson times are wall-clock stored as UTC-naive → always format in UTC.
const dtf = new Intl.DateTimeFormat('vi-VN', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: '2-digit' });
const tf = new Intl.DateTimeFormat('vi-VN', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hour12: false });
const dmf = new Intl.DateTimeFormat('vi-VN', { timeZone: 'UTC', day: '2-digit', month: '2-digit' });

export function fmtSessionTime(startAtIso: string, durationMin: number): string {
  const start = new Date(startAtIso);
  const end = new Date(start.getTime() + durationMin * 60_000);
  return `${tf.format(start)}–${tf.format(end)}`;
}
export function fmtDayLabel(startAtIso: string): string {
  return dtf.format(new Date(startAtIso));
}
/** YYYY-MM-DD (UTC) grouping key */
export function dayKey(startAtIso: string): string {
  return new Date(startAtIso).toISOString().slice(0, 10);
}
/** [from,to] ISO bounds for the single UTC day containing `d` */
export function dayRange(d: Date): { from: string; to: string } {
  const floor = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const next = new Date(floor.getTime() + 86_400_000);
  return { from: floor.toISOString(), to: next.toISOString() };
}
/** [from,to] ISO bounds for the 7-day window starting Monday of the anchor week (UTC) */
export function weekRange(anchor: Date): { from: string; to: string } {
  const d = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), anchor.getUTCDate()));
  const iso = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  const monday = new Date(d.getTime() - (iso - 1) * 86_400_000);
  const sunEnd = new Date(monday.getTime() + 7 * 86_400_000 - 1);
  return { from: monday.toISOString(), to: sunEnd.toISOString() };
}
/** "30/06 – 06/07" label for a week [from,to] (UTC) */
export function fmtWeekRange({ from, to }: { from: string; to: string }): string {
  return `${dmf.format(new Date(from))} – ${dmf.format(new Date(to))}`;
}
/** True if `anchor` falls in the same Monday–Sunday week as now (UTC). `now` defaults to the (possibly simulated) server clock. */
export function isCurrentWeek(anchor: Date, now: Date = serverNow()): boolean {
  return weekRange(anchor).from === weekRange(now).from;
}
// VN short weekday labels, indexed by Date#getUTCDay() (0=Sunday..6=Saturday)
const VN_SHORT_WEEKDAY = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'] as const;
/** Short VN weekday label ("T2".."T7"/"CN") for a "YYYY-MM-DD" date key (UTC).
 *  The timetable/attendance grid's own format (`AttendanceGrid.tsx`) — leave
 *  it alone. The progress feature (D1-D5) uses `weekdayLabel` below instead. */
export function shortWeekday(dateKey: string): string {
  return VN_SHORT_WEEKDAY[new Date(`${dateKey}T00:00:00Z`).getUTCDay()]!;
}
// VN weekday labels used across the progress feature's own mocks (D1-D5),
// indexed by Date#getUTCDay() — "Th 2".."Th 7"/"CN", WITH the space. A
// deliberately separate table from `VN_SHORT_WEEKDAY` above: the two features
// were ruled to keep their own mock-matched formats rather than converge on
// one (controller ruling, fix round 1) — the timetable/attendance grid stays
// "T5", the progress tab and its review editor render "Th 5".
const PROGRESS_WEEKDAY = ['CN', 'Th 2', 'Th 3', 'Th 4', 'Th 5', 'Th 6', 'Th 7'] as const;
/** "Th 5"/"CN" weekday label for either a full ISO date-time string
 *  (`startAt`, already carrying a `Z`/offset) or a bare "YYYY-MM-DD" date
 *  key (a sliced `startAt`) — slicing to the date part first and forcing a
 *  `T00:00:00Z` suffix, exactly like `shortWeekday` above, is what keeps a
 *  bare date-only string from parsing as LOCAL time in Safari (INVARIANTS
 *  §2) instead of UTC. Progress feature only (D1-D5 mocks) — see
 *  `shortWeekday` above for the timetable's own format. */
export function weekdayLabel(iso: string): string {
  return PROGRESS_WEEKDAY[new Date(`${iso.slice(0, 10)}T00:00:00Z`).getUTCDay()]!;
}
/** "Th 2".."CN" for the API's `dayOfWeek` (1=Mon..7=Sun) — the progress
 *  feature's own slot-schedule breadcrumb (D2) needs the same "Th N" table
 *  as `weekdayLabel` above but starts from a 1-7 day-of-week number rather
 *  than a date, so it can't reuse that function directly. `dow % 7` lines
 *  the 1-7 numbering up with `PROGRESS_WEEKDAY`'s own Date#getUTCDay()
 *  (0=Sun) indexing: 7 % 7 === 0 → 'CN', 1..6 fall through unchanged. */
export function weekdayLabelFromDow(dow: number): string {
  return PROGRESS_WEEKDAY[dow % 7]!;
}
/** The UTC calendar month of `now`, as "YYYY-MM". */
export function currentMonth(now: Date): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * The month before `ym` ("YYYY-MM"), wrapping across the year boundary.
 * Chốt sổ tháng M prepays M and settles M-1's attendance, so M-1 is the month
 * being reconciled (where un-marked sessions actually block the close).
 */
export function prevMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 2, 1)); // m-1 (0-indexed) minus 1 more = previous month
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * The month a teacher would be writing monthly reviews FOR right now: last
 * month until the 25th, this month from the 25th on (THI-105). Date-only UTC
 * math on the server clock, never vnTime (INVARIANTS §2) — shared by the
 * slot entry button (TimetablePage) and the workbench "Cần chú ý" nudge
 * (useAttentionItems), which must agree on the window without duplicating it.
 */
export function reviewMonthFor(now: Date): string {
  const month = currentMonth(now);
  return now.getUTCDate() < 25 ? prevMonth(month) : month;
}

/**
 * "9/2026" from a 'YYYY-MM' key — non-padded month, always WITH the year
 * (unlike `owedMonthFmt`'s same-year-elides-year rule): review copy
 * routinely nudges about last month's work into the next month, so it must
 * never read as "this year" by omission. Deliberately BARE, no "tháng"
 * prefix: every i18n string that interpolates this already carries its own
 * "tháng"/"Tháng" (or, in English, "for"/"in") right before {{month}} — a
 * formatter that also prepended the word produced "Nhận xét tháng tháng
 * 8/2026" (caught in browser QA). The one place a raw 'YYYY-MM' key becomes
 * displayed text — seven i18n strings across five files used to interpolate
 * the key as-is ("Nhận xét tháng 2026-09"); they all format through here now
 * (fix wave C1/F3/I2, corrected per controller ruling on the doubled word).
 */
export function formatMonthKey(ym: string): string {
  const [y, m] = ym.split('-');
  return `${Number(m)}/${y}`;
}

/** [from,to] ISO bounds for the calendar month `ym` ("YYYY-MM", UTC) */
export function monthRange(ym: string): { from: string; to: string } {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return {
    from: new Date(Date.UTC(y, m - 1, 1)).toISOString(),
    to: new Date(Date.UTC(y, m, 1)).toISOString(),
  };
}
